import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Cookies from 'js-cookie';
import { COOKIES } from '@/types';
import { OPCO_ID } from '@/models/opco/opco.shared.types';
import { useClientSideOpcoContext } from '@/context/client-side-opco/opco-context';
import useBasePath from '@/hooks/use-base-path';
import Selector from '@/components/selector/selector';
import styles from './opco-selectors.module.scss';

const OpcoSelectors = () => {
  const { reload } = useRouter();
  const { setLocale } = useBasePath();

  const { opcoId, availableAviosMarketsCookie, availableLocales } =
    useClientSideOpcoContext();

  const availableLocalesOptions = availableLocales.map(loc => ({
    isDefault: Cookies.get(COOKIES.AVIOS_OPCO_LANG) === loc,
    value: loc,
    label: loc,
  }));

  const initialMarketOptions =
    availableAviosMarketsCookie?.map(market => ({
      isDefault: Cookies.get(COOKIES.AVIOS_OPCO) === market,
      value: market,
      label: market,
    })) ?? [];

  const [marketOptions, setMarketOptions] = useState(initialMarketOptions);

  interface MarketOption {
    isDefault: boolean;
    value: string;
    label: string;
  }

  const updateMarketOptions = (
    removeCondition: (option: MarketOption) => boolean,
    newDefaultValue: string,
  ): void => {
    setMarketOptions(previousOptions => {
      const filteredOptions = previousOptions.filter(
        option => !removeCondition(option),
      );
      return filteredOptions.map(option => ({
        ...option,
        isDefault: option.value === newDefaultValue,
      }));
    });
  };

  type UpdateAndSetDefault = (
    marketToRemove: string,
    newDefaultMarket: string,
  ) => void;

  const updateAndSetDefault: UpdateAndSetDefault = (
    marketToRemove,
    newDefaultMarket,
  ) => {
    updateMarketOptions(
      (option: MarketOption) => option.value === marketToRemove,
      newDefaultMarket,
    );
  };

  useEffect(() => {
    if (opcoId === OPCO_ID.IB) updateAndSetDefault('GB', 'ES');
    if (opcoId === OPCO_ID.BA) updateAndSetDefault('ES', 'GB');
    if (opcoId === OPCO_ID.EI) updateAndSetDefault('', 'IE');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opcoId]);

  const handleOpcoChange = (value: string) => {
    Cookies.set(COOKIES.AVIOS_OPCO, value);
    switch (value) {
      case 'IB': {
        Cookies.set(COOKIES.AVIOS_OPCO_MARKET, 'ES');
        updateMarketOptions(option => option.value === 'GB', 'ES');
        break;
      }
      case 'BA': {
        Cookies.set(COOKIES.AVIOS_OPCO_MARKET, 'BA');
        Cookies.set(COOKIES.AVIOS_OPCO_LANG, 'en-GB');
        updateMarketOptions(option => option.value === 'ES', 'BA');
        break;
      }
      case 'EI': {
        Cookies.set(COOKIES.AVIOS_OPCO_MARKET, 'IE');
        Cookies.set(COOKIES.AVIOS_OPCO_LANG, 'en-IE');
        updateMarketOptions(() => false, 'IE');
        break;
      }
      default: {
        break;
      }
    }
    reload();
  };

  const handleMarketChange = (value: string) => {
    Cookies.set(COOKIES.AVIOS_OPCO_MARKET, value);
    reload();
  };

  const handleLanguageChange = (value: string) => {
    Cookies.set(COOKIES.AVIOS_OPCO_LANG, value);
    setLocale(value);
  };

  const selectorStyle = { background: '#0032a0', width: '200px' };

  return (
    <div className={styles['selector-strip']}>
      <span>
        <h5>Opco</h5>
        <Selector
          style={selectorStyle}
          values={[
            {
              isDefault: Cookies.get(COOKIES.AVIOS_OPCO) === 'BA',
              value: 'BA',
              label: 'Avios - BAEC',
            },
            {
              isDefault: Cookies.get(COOKIES.AVIOS_OPCO) === 'IB',
              value: 'IB',
              label: 'Iberia',
            },
            {
              isDefault: Cookies.get(COOKIES.AVIOS_OPCO) === 'EI',
              value: 'EI',
              label: 'Aerclub',
            },
          ]}
          defaultValue={Cookies.get(COOKIES.AVIOS_OPCO)}
          onValueChange={handleOpcoChange}
        />
      </span>
      <span>
        <h5>Market</h5>
        <Selector
          style={selectorStyle}
          values={marketOptions}
          defaultValue={Cookies.get(COOKIES.AVIOS_OPCO_MARKET)}
          onValueChange={handleMarketChange}
        />
      </span>
      <span>
        <h5>Language</h5>
        <Selector
          style={selectorStyle}
          values={availableLocalesOptions}
          defaultValue={Cookies.get(COOKIES.AVIOS_OPCO_LANG)}
          onValueChange={handleLanguageChange}
        />
      </span>
    </div>
  );
};

export default OpcoSelectors;
